import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Form, Input, Button, Typography, Flex, Segmented, message } from "antd";
import loginImg from "../../assets/images/login-img.svg";
import { Logo } from "../../assets/CustomIcon";
import CaptchaWidget from "../../components/Auth/CaptchaWidget";
import { config } from "../../config";

import "../../assets/styles/component_styles/validation.css";
import "../../assets/styles/component_styles/login.css";
import { isValidEmail, isValidPhone, hashPassword } from "../../utils/appUtils";
import { VALIDATION_MESSAGES } from "../../utils/constant";
import { loginUser } from "../../redux/userSlice";

const { Title, Text } = Typography;

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [loginType, setLoginType] = useState("email"); // "email" or "phone"
  const [formData, setFormData] = useState({
    value: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);
  const requireCaptcha = !!config.turnstileSiteKey;

  // Switch between email and phone
  const handleLoginTypeChange = (value) => {
    setLoginType(value);
    setFormData({ ...formData, value: "" }); // Clear value on switch
    setErrors({});
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ""
      });
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.value) {
      const errorMsg = loginType === "email" 
        ? VALIDATION_MESSAGES.EMAIL_INVALID 
        : VALIDATION_MESSAGES.PHONE_REQUIRED;
      setErrors({ value: errorMsg });
      return;
    }

    if (loginType === "email" && !isValidEmail(formData.value)) {
      setErrors({ value: VALIDATION_MESSAGES.EMAIL_INVALID });
      return;
    }

    if (loginType === "phone" && !isValidPhone(formData.value)) {
      setErrors({ value: VALIDATION_MESSAGES.PHONE_INVALID });
      return;
    }

    if (!formData.password) {
      setErrors({ password: VALIDATION_MESSAGES.PASSWORD_REQUIRED });
      return;
    }

    if (requireCaptcha && !captchaToken) {
      message.warning("Please complete the verification.");
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const hashedPassword = await hashPassword(formData.password);
      const submissionData = {
        type: loginType === "phone" ? "mobile" : "email",
        value: formData.value,
        password: hashedPassword,
        captchaToken: requireCaptcha ? captchaToken : undefined,
      };

      try {
        const result = await dispatch(loginUser(submissionData));
        setLoading(false);
        if (result && result.token) {
          message.success("Login successful!");
          navigate("/all-events");
        } else {
          setErrors({ password: VALIDATION_MESSAGES.LOGIN_INVALID_CREDENTIALS });
          if (requireCaptcha && captchaRef.current) captchaRef.current.reset();
        }
      } catch (err) {
        setLoading(false);
        console.error("Login dispatch error:", err);
        setErrors({ password: VALIDATION_MESSAGES.LOGIN_INVALID_CREDENTIALS });
        if (requireCaptcha && captchaRef.current) captchaRef.current.reset();
      }
    } catch (error) {
      setLoading(false);
      console.error("Hashing error", error);
      message.error("Error processing password.");
    }
  };

  return (
    <div className="loginPageRoot">
      <header className="topNavbar">
        <div className="container">
          <nav className="authNavbarWrap">
            <Title level={1} style={{ margin: 0 }} title="Pingvite">
              <Link to="/" className="siteLogo">
                <Logo />
              </Link>
            </Title>
            <Button
              className="menuMobileButton"
              type="primary"
            >
              <span className="material-symbols-outlined">menu</span>
            </Button>
          </nav>
        </div>
      </header>
      {/* Main content */}
      <main className="loginMain container">
        <div className="loginImage">
          <img src={loginImg} alt="Login" className="sideImage" />
        </div>

        {/* right form */}
        <div className="loginFormWrap">
          <div className="loginCard">
            <div className="loginCardInner">
              <div className="loginCardHeader">
                <Title level={3} className="title">
                  Discover exciting events near you
                </Title>
                <Text className="color-neutral">
                  Login to get exclusive offers.
                </Text>
              </div>

              {/* Login Type Toggle */}
              <div className="loginTypeToggle">
                <Segmented
                  options={[
                    {
                      label: (
                        <div className="segmentOption">
                          <span className="material-symbols-outlined">mail</span>
                          <span>Email</span>
                        </div>
                      ),
                      value: "email"
                    },
                    {
                      label: (
                        <div className="segmentOption">
                          <span className="material-symbols-outlined">phone</span>
                          <span>Phone</span>
                        </div>
                      ),
                      value: "phone"
                    }
                  ]}
                  value={loginType}
                  onChange={handleLoginTypeChange}
                  block
                  size="large"
                  className="loginSegmented"
                />
              </div>

              <Form name="login" layout="vertical" onFinish={handleSubmit}>
                <div className="loginInputWrapper">
                  {loginType === "email" ? (
                    <Form.Item
                      required
                      label="Email ID"
                      validateStatus={errors.value ? "error" : ""}
                      help={errors.value}
                      style={{ marginBottom: 12 }}
                      key="email-input"
                    >
                      <Input
                        name="value"
                        value={formData.value}
                        onChange={handleChange}
                        prefix={
                          <span className="material-symbols-outlined prefix-blue">
                            mail
                          </span>
                        }
                        placeholder="you@example.com"
                        size="large"
                        className="hasPrefixIconInput"
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      required
                      label="Phone Number"
                      validateStatus={errors.value ? "error" : ""}
                      help={errors.value}
                      style={{ marginBottom: 12 }}
                      key="phone-input"
                    >
                      <Input
                        name="value"
                        value={formData.value}
                        onChange={handleChange}
                        prefix={
                          <span className="material-symbols-outlined prefix-blue">
                            phone
                          </span>
                        }
                        placeholder="Enter 10-digit phone number"
                        size="large"
                        className="hasPrefixIconInput"
                        maxLength={10}
                      />
                    </Form.Item>
                  )}
                </div>

                <Form.Item
                  required
                  label="Password"
                  validateStatus={errors.password ? "error" : ""}
                  help={errors.password}
                  style={{ marginBottom: 12 }}
                >
                  <Input.Password
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    prefix={
                      <span className="material-symbols-outlined prefix-blue">
                        lock
                      </span>
                    }
                    placeholder="Enter password"
                    size="large"
                    className="hasPrefixIconInput"
                  />
                </Form.Item>
                <CaptchaWidget ref={captchaRef} onToken={setCaptchaToken} />
                <Form.Item style={{ marginTop: 18 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    className="btnGradient"
                    loading={loading}
                  >
                    {loading ? 'Signing in...' : 'Sign in'}
                  </Button>
                </Form.Item>
                <Flex align="center" justify="space-between">
                  <Link className="loginForgot" to="/forgot-password">
                    Forgot password?
                  </Link>
                </Flex>
              </Form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
